import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeModernIcon,
  UserCircleIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  RectangleGroupIcon,
  XMarkIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Role, useAuth } from '@/state/AuthContext';

interface NavItem {
  name: string;
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  roles: Role[];
  badge?: string;
}

const navItems: NavItem[] = [
  {
    name: '總覽儀表',
    to: '/',
    icon: HomeModernIcon,
    roles: ['medical_director', 'surgery', 'internal_med', 'customer_service', 'billing'],
  },
  {
    name: '營運佈告',
    to: '/ops',
    icon: RectangleGroupIcon,
    roles: ['medical_director', 'surgery', 'internal_med', 'customer_service', 'billing'],
    badge: '加護/手術',
  },
  {
    name: '任務清單',
    to: '/tasks',
    icon: ClipboardDocumentCheckIcon,
    roles: ['medical_director', 'surgery', 'internal_med', 'customer_service'],
    badge: '指示/檢驗',
  },
  {
    name: '病患',
    to: '/patients',
    icon: UserCircleIcon,
    roles: ['medical_director', 'surgery', 'internal_med'],
  },
  {
    name: '照護計畫',
    to: '/care',
    icon: ClipboardDocumentListIcon,
    roles: ['medical_director', 'surgery', 'internal_med'],
    badge: '查房',
  },
  {
    name: '預約排程',
    to: '/appointments',
    icon: CalendarDaysIcon,
    roles: ['medical_director', 'surgery', 'internal_med', 'customer_service'],
  },
  {
    name: '帳務',
    to: '/billing',
    icon: BanknotesIcon,
    roles: ['medical_director', 'billing', 'customer_service'],
    badge: '財務',
  },
  {
    name: '稽核紀錄',
    to: '/audit',
    icon: ClipboardDocumentListIcon,
    roles: ['medical_director', 'billing'],
    badge: '合規',
  },
  {
    name: '錯誤日誌',
    to: '/admin/errors',
    icon: ExclamationTriangleIcon,
    roles: ['medical_director', 'billing'],
    badge: 'Debug',
  },
  {
    name: '系統設定',
    to: '/settings',
    icon: Cog6ToothIcon,
    roles: ['medical_director'],
  },
];

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const { role, roleMeta } = useAuth();

  const disallowedMessage = '需要權限';

  const handleNavClick = () => {
    // Close mobile sidebar when navigating
    if (onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 border-r px-4 py-6 backdrop-blur-xl
        transform transition-transform duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-white/80 dark:bg-slate-900/70 border-white/60 dark:border-slate-700
      `}>
        <div className="flex items-center justify-between gap-2 px-2">
          <div className="flex items-center gap-2">
            <span className="rounded-2xl bg-gradient-to-br from-brand-blue to-brand-indigo p-2 text-white shadow-soft">
              <HomeModernIcon className="h-6 w-6" />
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-500">LKC 醫療</p>
              <h2 className="text-lg font-semibold text-slate-900">動物醫院</h2>
            </div>
          </div>
          
          {/* Mobile close button */}
          <button
            onClick={onMobileClose}
            className="rounded-lg p-2 hover:bg-slate-100 lg:hidden"
            aria-label="關閉選單"
          >
            <XMarkIcon className="h-6 w-6 text-slate-500" />
          </button>
          
          {/* Desktop role badge */}
          <div className="hidden rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 lg:block">
            {roleMeta?.label ?? '團隊成員'}
          </div>
        </div>

        <nav className="mt-10 flex flex-col gap-1 px-2">
          {navItems.map((item) => {
            const isActive =
              item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
            const Icon = item.icon;
            const isAllowed = role ? item.roles.includes(role) : false;
            return (
              <Link
                key={item.name}
                to={isAllowed ? item.to : '#'}
                onClick={isAllowed ? handleNavClick : undefined}
                className={`group flex items-center justify-between rounded-xl px-3 py-3 text-sm font-medium transition touch-manipulation min-h-[44px] ${
                  isActive
                    ? 'bg-gradient-to-r from-brand-blue to-brand-sky text-white shadow-soft'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200'
                } ${isAllowed ? '' : 'pointer-events-none opacity-50'}`}
                title={isAllowed ? undefined : disallowedMessage}
              >
                <span className="flex items-center gap-3">
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {item.name}
                </span>
                {item.badge && (
                  <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-slate-500 flex-shrink-0">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="mt-10 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-900 dark:bg-slate-800 p-4 text-white shadow-soft">
          <p className="text-sm font-semibold">即時洞察</p>
          <p className="mt-2 text-xs text-white/80 dark:text-slate-300">
            今日排定 24 位病患，尚有 4 位等待分級檢視，請保持團隊同步。
          </p>
          <button
            type="button"
            className="mt-4 w-full rounded-xl bg-white/90 dark:bg-slate-700 py-2 text-sm font-semibold text-slate-900 dark:text-slate-100 shadow touch-manipulation min-h-[44px]"
          >
            查看行程
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
